import { supabase } from './client';
import { authStore } from '@stores/session';

// ---------------------------------------------------------------------------
// Forum sub-categories (migration 0028)
// ---------------------------------------------------------------------------

export type ReviewSubcategory = {
  id: string;
  slug: string;
  name: string;
  sort_order: number;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

export async function fetchReviewSubcategories(): Promise<ReviewSubcategory[]> {
  const { data, error } = await db
    .from('forum_subcategories')
    .select('id, slug, name, sort_order')
    .eq('parent_category', 'reviews')
    .order('sort_order');
  if (error) throw error;
  return (data ?? []) as ReviewSubcategory[];
}

// Map from sub-category slug to the strain_reviews.format enum value.
// DHV lives in hardware_reviews, so it's excluded here.
export function subcategoryToFormat(
  slug: string,
): 'flower' | 'oil' | 'vape' | 'pastille' | 'avb' | null {
  switch (slug) {
    case 'flowers':   return 'flower';
    case 'vapes':     return 'vape';
    case 'oils':      return 'oil';
    case 'pastilles': return 'pastille';
    case 'avb':       return 'avb';
    default:          return null;
  }
}

// ---------------------------------------------------------------------------
// Strain reviews (consumables: flowers, vapes, oils, pastilles, AVB)
// ---------------------------------------------------------------------------

const REVIEW_COLUMNS =
  'id, profile_id, strain_id, forum_post_id, batch_number, pharmacy_slug, format, would_reorder, appearance, aroma, taste, effects, price, overall, is_featured, upvotes, created_at, appearance_notes, aroma_notes, taste_notes, effects_notes, price_notes, other_notes, price_paid_gbp, images';

export type StrainReview = {
  id: string;
  profile_id: string | null;
  strain_id: string;
  forum_post_id: string;
  batch_number: string | null;
  pharmacy_slug: string | null;
  format: 'flower' | 'oil' | 'vape' | 'pastille' | 'avb' | null;
  would_reorder: boolean | null;
  appearance: number;
  aroma: number;
  taste: number;
  effects: number;
  price: number;
  overall: number;
  is_featured: boolean;
  upvotes: number;
  created_at: string;
  appearance_notes: string | null;
  aroma_notes: string | null;
  taste_notes: string | null;
  effects_notes: string | null;
  price_notes: string | null;
  other_notes: string | null;
  price_paid_gbp: number | null;
  /** `private:<path>` references for the review-image gallery. Length 0..5, enforced at the DB. */
  images: string[];
};

export async function fetchFeaturedReviews(strainId: string): Promise<StrainReview[]> {
  const { data, error } = await db
    .from('strain_reviews')
    .select(REVIEW_COLUMNS)
    .eq('strain_id', strainId)
    .order('is_featured', { ascending: false })
    .order('upvotes', { ascending: false })
    .limit(10);
  if (error) throw error;
  const rows = (data ?? []) as StrainReview[];
  const featured = rows.filter((r) => r.is_featured);
  const topOther = rows.find((r) => !r.is_featured);
  return topOther ? [...featured, topOther] : featured;
}

export type NewStrainReviewInput = {
  strainId: string;
  title: string;
  subcategorySlug: 'flowers' | 'vapes' | 'oils' | 'pastilles' | 'avb';
  subcategoryId: string;
  batchNumber?: string;
  pharmacySlug?: string;
  wouldReorder?: boolean;
  pricePaidGbp?: number;
  appearance: number;
  aroma: number;
  taste: number;
  effects: number;
  price: number;
  appearanceNotes?: string;
  aromaNotes?: string;
  tasteNotes?: string;
  effectsNotes?: string;
  priceNotes?: string;
  otherNotes?: string;
  /**
   * `private:<path>` references extracted from the Other-notes
   * MarkdownComposer at submission time. Capped at 5 by the DB
   * (migration 0047). The form is responsible for de-duping and
   * truncating before passing through.
   */
  images?: string[];
};

// Compose the free-form forum_posts.body from structured fields so the
// thread view still reads as prose, search still returns useful hits,
// and moderators reading raw posts don't have to hop to strain_reviews.
function composeStrainReviewBody(input: NewStrainReviewInput): string {
  const parts: string[] = [];
  const push = (label: string, value?: string) => {
    const trimmed = value?.trim();
    if (trimmed) parts.push(`**${label}:** ${trimmed}`);
  };
  push('Appearance', input.appearanceNotes);
  push('Aroma',      input.aromaNotes);
  push('Taste',      input.tasteNotes);
  push('Effects',    input.effectsNotes);
  push('Price',      input.priceNotes);
  push('Other',      input.otherNotes);
  return parts.join('\n\n') || '(structured review — see ratings above)';
}

export async function createStrainReview(input: NewStrainReviewInput): Promise<StrainReview> {
  const profile = authStore.profile();
  if (!profile) throw new Error('Not signed in');

  const post = await db
    .from('forum_posts')
    .insert({
      profile_id: profile.id,
      category: 'reviews',
      subcategory_id: input.subcategoryId,
      strain_id: input.strainId,
      title: input.title,
      body: composeStrainReviewBody(input),
    })
    .select('id')
    .single();
  if (post.error) throw post.error;
  const forumPostId = (post.data as { id: string }).id;

  const review = await db
    .from('strain_reviews')
    .insert({
      profile_id: profile.id,
      strain_id: input.strainId,
      forum_post_id: forumPostId,
      batch_number: input.batchNumber?.trim() || null,
      pharmacy_slug: input.pharmacySlug || null,
      format: subcategoryToFormat(input.subcategorySlug),
      would_reorder: input.wouldReorder ?? null,
      appearance: input.appearance,
      aroma: input.aroma,
      taste: input.taste,
      effects: input.effects,
      price: input.price,
      appearance_notes: input.appearanceNotes?.trim() || null,
      aroma_notes:      input.aromaNotes?.trim()      || null,
      taste_notes:      input.tasteNotes?.trim()      || null,
      effects_notes:    input.effectsNotes?.trim()    || null,
      price_notes:      input.priceNotes?.trim()      || null,
      other_notes:      input.otherNotes?.trim()      || null,
      price_paid_gbp:   input.pricePaidGbp ?? null,
      images:           input.images ?? [],
    })
    .select(REVIEW_COLUMNS)
    .single();
  if (review.error) throw review.error;
  return review.data as StrainReview;
}

export async function voteForReview(reviewId: string): Promise<void> {
  const profile = authStore.profile();
  if (!profile) throw new Error('Not signed in');
  const { error } = await db
    .from('strain_review_votes')
    .insert({ review_id: reviewId, profile_id: profile.id });
  if (error) throw error;
}

export async function unvoteReview(reviewId: string): Promise<void> {
  const profile = authStore.profile();
  if (!profile) throw new Error('Not signed in');
  const { error } = await db
    .from('strain_review_votes')
    .delete()
    .eq('review_id', reviewId)
    .eq('profile_id', profile.id);
  if (error) throw error;
}

export async function fetchMyReviewVotes(reviewIds: string[]): Promise<Set<string>> {
  const profile = authStore.profile();
  if (!profile || reviewIds.length === 0) return new Set();
  const { data, error } = await db
    .from('strain_review_votes')
    .select('review_id')
    .eq('profile_id', profile.id)
    .in('review_id', reviewIds);
  if (error) throw error;
  return new Set(((data ?? []) as { review_id: string }[]).map((r) => r.review_id));
}

// ---------------------------------------------------------------------------
// Hardware reviews (DHV — dry herb vaporisers)
// ---------------------------------------------------------------------------

const HW_COLUMNS =
  'id, profile_id, forum_post_id, brand, model, usage, effectiveness, maintainability, price, price_paid_gbp, other_notes, overall, is_featured, upvotes, created_at';

export type HardwareReview = {
  id: string;
  profile_id: string | null;
  forum_post_id: string;
  brand: string;
  model: string;
  usage: 'home' | 'outdoors' | 'both' | null;
  effectiveness: number;
  maintainability: number;
  price: number;
  price_paid_gbp: number | null;
  other_notes: string | null;
  overall: number;
  is_featured: boolean;
  upvotes: number;
  created_at: string;
};

export type NewHardwareReviewInput = {
  title: string;
  subcategoryId: string;
  brand: string;
  model: string;
  usage: 'home' | 'outdoors' | 'both';
  effectiveness: number;
  maintainability: number;
  price: number;
  pricePaidGbp?: number;
  otherNotes?: string;
};

function composeHardwareReviewBody(input: NewHardwareReviewInput): string {
  const parts = [
    `**Brand:** ${input.brand}`,
    `**Model:** ${input.model}`,
    `**Usage:** ${input.usage === 'home' ? 'At Home' : input.usage === 'outdoors' ? 'Outdoors' : 'Both'}`,
  ];
  if (input.otherNotes?.trim()) parts.push(`**Other:** ${input.otherNotes.trim()}`);
  return parts.join('\n\n');
}

export async function createHardwareReview(
  input: NewHardwareReviewInput,
): Promise<HardwareReview> {
  const profile = authStore.profile();
  if (!profile) throw new Error('Not signed in');

  const post = await db
    .from('forum_posts')
    .insert({
      profile_id: profile.id,
      category: 'reviews',
      subcategory_id: input.subcategoryId,
      title: input.title,
      body: composeHardwareReviewBody(input),
    })
    .select('id')
    .single();
  if (post.error) throw post.error;
  const forumPostId = (post.data as { id: string }).id;

  const review = await db
    .from('hardware_reviews')
    .insert({
      profile_id: profile.id,
      forum_post_id: forumPostId,
      brand: input.brand.trim(),
      model: input.model.trim(),
      usage: input.usage,
      effectiveness: input.effectiveness,
      maintainability: input.maintainability,
      price: input.price,
      price_paid_gbp: input.pricePaidGbp ?? null,
      other_notes: input.otherNotes?.trim() || null,
    })
    .select(HW_COLUMNS)
    .single();
  if (review.error) throw review.error;
  return review.data as HardwareReview;
}

// ---------------------------------------------------------------------------
// Strain images (unchanged from Phase 5 — kept in this module for locality)
// ---------------------------------------------------------------------------

export type StrainImage = {
  id: string;
  strain_id: string;
  profile_id: string;
  url: string;
  caption: string | null;
  approved_at: string | null;
  created_at: string;
};

export async function fetchStrainImages(strainId: string): Promise<StrainImage[]> {
  const { data, error } = await db
    .from('strain_images')
    .select('id, strain_id, profile_id, url, caption, approved_at, created_at')
    .eq('strain_id', strainId)
    .not('approved_at', 'is', null)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data ?? []) as StrainImage[];
}

export async function submitStrainImage(
  strainId: string,
  url: string,
  caption?: string,
): Promise<void> {
  const profile = authStore.profile();
  if (!profile) throw new Error('Not signed in');
  const { error } = await db.from('strain_images').insert({
    strain_id: strainId,
    profile_id: profile.id,
    url: url.trim(),
    caption: caption?.trim() || null,
  });
  if (error) throw error;
}
